import { Popper, styled } from '@mui/material'
import { UnfoldMoreRounded, UnfoldLessRounded } from '@mui/icons-material'
import {
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { Modifier } from '@popperjs/core'
import type { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset'
import { colorPalette } from '../../../style/colorPalette'

const EXPANDED_Z_INDEX = 10
const TOGGLE_BUTTON_SIZE = 20
const TOGGLE_BUTTON_OFFSET = 2
const SCROLLABLE_LEFT_PADDING = TOGGLE_BUTTON_SIZE + 8
const EXPANDED_BORDER_RADIUS = 4
const EXPANDED_MAX_HEIGHT = '80vh'

const offsetFn: OffsetsFunction = ({ reference }) => [0, -reference.height]

const sameWidthModifier: Modifier<'sameWidth', object> = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`
  },
  effect: ({ state }) => {
    const ref = state.elements.reference
    if (ref instanceof HTMLElement) {
      state.elements.popper.style.width = `${ref.offsetWidth}px`
    }
  },
}

type Props = {
  value: string | undefined
}

export const ExpandableText = ({ value }: Props) => {
  const [expanded, setExpanded] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  const hasContent = !!value && value.trim().length > 0

  useEffect(() => {
    if (!hasContent && expanded) setExpanded(false)
  }, [hasContent, expanded])

  const handleToggle = (e: ReactMouseEvent) => {
    e.stopPropagation()
    setExpanded(prev => !prev)
  }

  return (
    <>
      <CollapsedWrapper ref={anchorRef}>
        <TextContent>{value}</TextContent>
        {hasContent && !expanded && (
          <ToggleButton type="button" onClick={handleToggle}>
            <UnfoldMoreRounded fontSize="small" />
          </ToggleButton>
        )}
      </CollapsedWrapper>
      <Popper
        open={expanded}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: EXPANDED_Z_INDEX }}
        modifiers={[
          { name: 'flip', enabled: false },
          { name: 'offset', options: { offset: offsetFn } },
          sameWidthModifier,
        ]}
      >
        <ExpandedOverlay>
          <TextContent>{value}</TextContent>
          <ToggleButton type="button" onClick={handleToggle}>
            <UnfoldLessRounded fontSize="small" />
          </ToggleButton>
        </ExpandedOverlay>
      </Popper>
    </>
  )
}

const CollapsedWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
})

const ExpandedOverlay = styled('div')({
  position: 'relative',
  width: '100%',
  maxHeight: EXPANDED_MAX_HEIGHT,
  overflow: 'hidden',
  background: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.skyBlue[7]}`,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  borderRadius: EXPANDED_BORDER_RADIUS,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
})

const TextContent = styled('div')({
  color: colorPalette.monotone[9],
  fontSize: '14px',
  lineHeight: 1.5,
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  width: '100%',
  height: '100%',
  maxHeight: EXPANDED_MAX_HEIGHT,
  boxSizing: 'border-box',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: `0 0 0 ${SCROLLABLE_LEFT_PADDING}px`,
})

const ToggleButton = styled('button')({
  position: 'absolute',
  top: TOGGLE_BUTTON_OFFSET,
  left: TOGGLE_BUTTON_OFFSET,
  width: TOGGLE_BUTTON_SIZE,
  height: TOGGLE_BUTTON_SIZE,
  padding: 0,
  border: 'none',
  borderRadius: EXPANDED_BORDER_RADIUS,
  background: 'rgba(255,255,255,0.7)',
  color: colorPalette.monotone[7],
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    background: 'rgba(0,0,0,0.06)',
  },
})
